import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { IRichBlockSelect, IRichBlockSelectOption, RichBlockActionActivation } from '@pure1/data';

/** The one placeholder the contract defines in a submit template. */
const SELECTION_PLACEHOLDER = '{selection}';

/**
 * The only block that takes input (R2.7): pick options, then ask about them.
 *
 * Selection is deliberately held in a component signal and nowhere else (FR-042). It is never
 * persisted, never sent, and never read back into model context — the durable record of a choice
 * is the chat message it produced, so a reload renders this with nothing selected and that is
 * correct rather than a lost draft.
 *
 * Submitting is not a new kind of operation (FR-043): it emits the same prompt activation an
 * action button emits, so it travels the same path and obeys the same verbatim-display rule.
 */
@Component({
    selector: 'copilot-rich-block-select',
    templateUrl: './select-block.component.html',
    styleUrl: './select-block.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
})
export class SelectBlockComponent {
    readonly block = input.required<IRichBlockSelect>();

    readonly activateAction = output<RichBlockActionActivation>();

    /** Chosen option values. A Set would not notify, so this is a plain array in a signal. */
    private readonly selected = signal<string[]>([]);

    readonly isMulti = computed(() => this.block().mode === 'multi');

    /** FR-044: single needs exactly one, multiple needs at least one. */
    readonly canSubmit = computed(() => {
        const count = this.selected().length;
        return this.isMulti() ? count >= 1 : count === 1;
    });

    isSelected(option: IRichBlockSelectOption): boolean {
        return this.selected().includes(option.value);
    }

    toggle(option: IRichBlockSelectOption): void {
        if (!this.isMulti()) {
            // Single mode is re-selectable but not clearable by clicking the chosen option: an
            // empty selection is not a state the reader can usefully be in here.
            this.selected.set([option.value]);
            return;
        }
        this.selected.update(current =>
            current.includes(option.value) ? current.filter(v => v !== option.value) : [...current, option.value],
        );
    }

    submit(): void {
        if (!this.canSubmit()) {
            return;
        }
        this.activateAction.emit({ kind: 'prompt', query: this.query() });
    }

    /**
     * The message the selection composes.
     *
     * Labels, not values: the value is stable identity the user never sees, and a question built
     * from opaque ids would read as nonsense in the transcript. Options are named in the order
     * the block declared them, not the order they were clicked, so the same set of choices always
     * produces the same message.
     */
    private query(): string {
        const chosen = this.selected();
        const labels = this.block()
            .options.filter(option => chosen.includes(option.value))
            .map(option => option.label);
        const template = this.block().submit.query_template;

        // A template missing its placeholder would otherwise send the bare sentence with the
        // selection silently dropped — worse than appending, which at least carries the choice.
        return template.includes(SELECTION_PLACEHOLDER)
            ? template.replace(SELECTION_PLACEHOLDER, labels.join(', '))
            : `${template} ${labels.join(', ')}`.trim();
    }
}
